import React, { useEffect, useMemo, useState } from "react";
import { Button, Card, CardBody, Col, Input, Label, Nav, NavItem, NavLink, Row } from "reactstrap";
import classnames from "classnames";
import { toast } from "react-toastify";

import LoadingSpinner from "../../../../Components/Common/Spinner";
import TableContainer from "../../../../Components/Common/TableContainer";
import AppModal from "../../../../Components/Common/AppModal";
import DeleteModal from "../../../../Components/Common/DeleteModal";
import SettingsPanel from "../../components/SettingsPanel";
import { companySiteLocationListApi } from "../../../../helpers/fakebackend_helper";
import { KioskQrImage } from "../../../Prototyping/clockShared/KioskQr";
import { DEVICE_INFO_FIELDS, formatDeviceValue } from "../../../Prototyping/clockShared/deviceInfo";

import {
  kioskEnroll,
  kioskDeviceList,
  kioskDeviceDelete,
  kioskDeviceUpdate,
  kioskDeviceSetup,
  kioskSetupLink,
  kioskPageUrl,
  kioskShortCode,
  pickList,
  pickToken,
} from "../../../Prototyping/clockShared/staffClockApi";

// Company Settings > Clock-In Kiosks (Change #100094).
//
// The /clock-in-out tablet authenticates with an ATTENDANCE_KIOSK token. Before
// this page the only way to get one was to log in as a manager ON the tablet and
// press "Enrol this device". Here a manager mints the token from any browser,
// then either opens the setup link on the tablet (the token rides in the URL
// fragment, see kioskSetupLink) or pastes the token into the kiosk setup screen.
//
// Uses StaffClockKioskController enroll / devicelist / devicedelete
// (AUTHCAP2356X / 2357X / 2358X, personal login required). The token is shown
// ONCE, in the reveal modal; devicelist never returns it.

const formatDateTime = (value) => {
  if (!value) return "-";
  const d = new Date(String(value).replace(" ", "T"));
  return isNaN(d.getTime()) ? value : d.toLocaleString();
};

// Change #100098 - the three ways to set a tablet up, QR first because it
// needs nothing typed on the tablet.
const REVEAL_TABS = [
  { id: "qr", label: "QR Code", icon: "ri-qr-code-line" },
  { id: "token", label: "Copy & Paste Token", icon: "ri-key-2-line" },
  { id: "link", label: "Use Setup Link", icon: "ri-link" },
];

const copyText = async (text, what) => {
  try {
    await navigator.clipboard.writeText(text);
    toast.success(`${what} copied to clipboard`);
  } catch (e) {
    toast.error(`Could not copy — select the ${what.toLowerCase()} and copy it manually`);
  }
};

// Read-only value with a Copy button, used in the reveal modal.
function CopyRow({ label, value, what }) {
  return (
    <div className="mb-3">
      <Label className="form-label mb-1">{label}</Label>
      <div className="d-flex align-items-center gap-2">
        <code
          className="flex-grow-1 p-2 bg-light rounded user-select-all"
          style={{ wordBreak: "break-all" }}
        >
          {value}
        </code>
        <Button color="primary" onClick={() => copyText(value, what)}>
          <i className="ri-file-copy-line align-bottom me-1" /> Copy
        </Button>
      </div>
    </div>
  );
}

// Change #100097 - optional premises for a kiosk (Settings > Company > Site Locations).
function SiteLocationPicker({ id, sites, value, onChange }) {
  return (
    <div className="mt-3">
      <Label htmlFor={id} className="form-label">
        Location
      </Label>
      <Input id={id} type="select" value={value} onChange={(e) => onChange(e.target.value)}>
        <option value="">No location</option>
        {sites.map((site) => (
          <option key={site.Id} value={String(site.Id)}>
            {site.Name}
          </option>
        ))}
      </Input>
      <div className="text-muted fs-12 mt-2">
        {sites.length
          ? "Which of your premises this tablet is at."
          : "Add your premises in Settings > Company > Site Locations to pick one here."}
      </div>
    </div>
  );
}

// Change #100101 - what the tablet last reported about itself, plus the public
// IP the API saw. Local IP and serial number are listed as unavailable on
// purpose: no web page can read them, so a blank would suggest a fault.
function KioskDebugPanel({ row }) {
  const info = useMemo(() => {
    try {
      return row?.StaffClockKiosk_DeviceInfoJson ? JSON.parse(row.StaffClockKiosk_DeviceInfoJson) : null;
    } catch {
      return null;
    }
  }, [row]);

  const pendingDelta = row && !("StaffClockKiosk_DeviceInfoJson" in row);

  const Line = ({ label, children }) => (
    <tr>
      <th className="text-muted fw-normal text-nowrap pe-3" style={{ width: "32%" }}>
        {label}
      </th>
      <td style={{ wordBreak: "break-word" }}>{children}</td>
    </tr>
  );

  return (
    <div>
      {pendingDelta ? (
        <div className="alert alert-warning py-2">
          Device details are not recorded for your company yet (waiting for a system update).
        </div>
      ) : !info ? (
        <div className="alert alert-info py-2">
          This kiosk has not reported its device details yet. Open or refresh the kiosk page on the
          tablet; it reports on load and every 30 minutes.
        </div>
      ) : null}

      <table className="table table-sm mb-2">
        <tbody>
          <Line label="Kiosk code">{kioskShortCode(row?.StaffClockKiosk_Id) || "-"}</Line>
          <Line label="Last seen">{formatDateTime(row?.StaffClockKiosk_LastSeenDateTime)}</Line>
          <Line label="Details reported">{formatDateTime(row?.StaffClockKiosk_DeviceInfoDateTime)}</Line>
          <Line label="Public IP">{row?.StaffClockKiosk_LastSeenIpAddress || "-"}</Line>
          {info &&
            DEVICE_INFO_FIELDS.map(([key, label]) => (
              <Line key={key} label={label}>
                {key === "userAgent" ? (
                  <code className="fs-12">{formatDeviceValue(key, info[key])}</code>
                ) : (
                  formatDeviceValue(key, info[key])
                )}
              </Line>
            ))}
          <Line label="Local IP">
            <span className="text-muted">Not available: browsers hide it from web pages</span>
          </Line>
          <Line label="Serial number / IMEI">
            <span className="text-muted">Not available: browsers never expose it</span>
          </Line>
        </tbody>
      </table>
      <div className="text-muted fs-12">
        Make is worked out from the model code, so it can be blank for less common brands. Model
        and OS version come from Chrome, Edge and Samsung Internet; Safari and Firefox do not share
        them.
      </div>
    </div>
  );
}

export default function ClockInKiosksSettings() {
  const [kiosks, setKiosks] = useState([]);
  const [loading, setLoading] = useState(true);

  const [createOpen, setCreateOpen] = useState(false);
  const [label, setLabel] = useState("");
  const [siteLocationId, setSiteLocationId] = useState("");
  const [creating, setCreating] = useState(false);

  // { label, token, kioskUrl } shown once after creation (null when closed).
  const [reveal, setReveal] = useState(null);
  const [revealTab, setRevealTab] = useState("qr");

  // Change #100098 - the company's own kiosk address ({prefix}.portal.crmit.app)
  // from devicelist; this host until the backend returning it is live.
  const [kioskUrl, setKioskUrl] = useState(kioskPageUrl());

  // Change #100097 - the company's premises, for the kiosk's location.
  const [sites, setSites] = useState([]);
  const [editTarget, setEditTarget] = useState(null); // { id, label, siteLocationId }
  const [saving, setSaving] = useState(false);

  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const fetchKiosks = async () => {
    setLoading(true);
    const res = await kioskDeviceList();
    if (res.ok) {
      setKiosks(pickList(res.data, ["staffclockkiosk_list"]));
      if (res.data?.staffclockkiosk_urls?.KioskUrl) setKioskUrl(res.data.staffclockkiosk_urls.KioskUrl);
    } else {
      toast.error(res.message || "Failed to load kiosks");
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchKiosks();
    let cancelled = false;
    companySiteLocationListApi()
      .then((res) => {
        if (cancelled) return;
        setSites(
          (res?.companysitelocation_list || []).filter((x) => String(x.FlagActive) !== "0")
        );
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  // One location is the obvious default; otherwise the flagged default, if any.
  const defaultSiteId = () => {
    if (sites.length === 1) return String(sites[0].Id);
    const flagged = sites.find((x) => String(x.FlagDefault) === "1");
    return flagged ? String(flagged.Id) : "";
  };

  const openCreate = () => {
    setLabel("");
    setSiteLocationId(defaultSiteId());
    setCreateOpen(true);
  };

  const handleSaveEdit = async () => {
    if (!editTarget || !editTarget.label.trim() || saving) return;
    setSaving(true);
    const res = await kioskDeviceUpdate(editTarget.id, {
      Label: editTarget.label.trim(),
      CompanySiteLocation_Id: editTarget.siteLocationId ? Number(editTarget.siteLocationId) : null,
    });
    setSaving(false);
    if (res.ok) {
      toast.success("Kiosk updated");
      setEditTarget(null);
      fetchKiosks();
    } else {
      toast.error(res.message || "Could not update the kiosk");
    }
  };

  const handleCreate = async () => {
    const trimmed = label.trim();
    if (!trimmed || creating) return;
    setCreating(true);
    // No DeviceIdentifier: the token is minted here, not on the tablet.
    const res = await kioskEnroll(trimmed, null, siteLocationId || null);
    setCreating(false);
    const token = pickToken(res.data);
    if (!res.ok || !token) {
      toast.error(res.message || "Could not create the kiosk token");
      return;
    }
    setCreateOpen(false);
    setRevealTab("qr");
    setReveal({
      label: trimmed,
      token,
      kioskUrl: res.data?.staffclockkiosk_data?.KioskUrl || kioskUrl,
    });
    fetchKiosks();
  };

  // Change #100098 - reopen the setup modal for an existing kiosk.
  const [openingSetupId, setOpeningSetupId] = useState(null);
  const openSetup = async (kioskId, kioskLabel) => {
    if (!kioskId || openingSetupId) return;
    setOpeningSetupId(kioskId);
    const res = await kioskDeviceSetup(kioskId);
    setOpeningSetupId(null);
    const token = pickToken(res.data);
    if (!res.ok || !token) {
      toast.error(res.message || "Could not load this kiosk's setup code");
      return;
    }
    setEditTarget(null);
    setRevealTab("qr");
    setReveal({
      label: kioskLabel,
      token,
      kioskUrl: res.data?.staffclockkiosk_data?.KioskUrl || kioskUrl,
      again: true,
    });
  };

  const handleRevoke = async () => {
    const id = deleteTarget?.StaffClockKiosk_Id;
    if (!id) return;
    setDeleting(true);
    const res = await kioskDeviceDelete(id);
    setDeleting(false);
    if (res.ok) {
      toast.success("Kiosk revoked");
      setDeleteTarget(null);
      fetchKiosks();
    } else {
      toast.error(res.message || "Could not revoke the kiosk");
    }
  };

  const columns = useMemo(
    () => [
      {
        Header: "Kiosk",
        accessor: "StaffClockKiosk_Label",
        filterable: false,
        Cell: (card) => (
          <div>
            <span className="fw-medium">
              {card.row.original?.StaffClockKiosk_Label || "-"}
            </span>
            {/* The same short code the tablet shows in its corner, so support
                can match "which kiosk is this?" at a glance. */}
            <div className="text-muted fs-12">
              {kioskShortCode(card.row.original?.StaffClockKiosk_Id)}
            </div>
          </div>
        ),
      },
      {
        Header: "Location",
        accessor: "CompanySiteLocation_Name",
        filterable: false,
        Cell: (card) =>
          card.row.original?.CompanySiteLocation_Name ? (
            <span>{card.row.original.CompanySiteLocation_Name}</span>
          ) : (
            <span className="text-muted">-</span>
          ),
      },
      {
        Header: "Device",
        accessor: "StaffClockKiosk_DeviceIdentifier",
        filterable: false,
        Cell: (card) => (
          <code className="text-muted">
            {card.row.original?.StaffClockKiosk_DeviceIdentifier || "-"}
          </code>
        ),
      },
      {
        Header: "Last seen",
        accessor: "StaffClockKiosk_LastSeenDateTime",
        filterable: false,
        Cell: (card) =>
          card.row.original?.StaffClockKiosk_LastSeenDateTime ? (
            <span>{formatDateTime(card.row.original.StaffClockKiosk_LastSeenDateTime)}</span>
          ) : (
            <span className="badge bg-warning-subtle text-warning">Never used</span>
          ),
      },
      {
        Header: "Created",
        accessor: "StaffClockKiosk_CreatedDateTime",
        filterable: false,
        Cell: (card) => (
          <span className="text-muted">
            {formatDateTime(card.row.original?.StaffClockKiosk_CreatedDateTime)}
          </span>
        ),
      },
      {
        Header: "Action",
        filterable: false,
        Cell: (card) => (
          <div className="d-flex gap-2">
            <Button
              color="soft-primary"
              size="sm"
              onClick={() =>
                setEditTarget({
                  id: card.row.original?.StaffClockKiosk_Id,
                  label: card.row.original?.StaffClockKiosk_Label || "",
                  siteLocationId: card.row.original?.StaffClockKiosk_CompanySiteLocation_Id
                    ? String(card.row.original.StaffClockKiosk_CompanySiteLocation_Id)
                    : "",
                  row: card.row.original, // Change #100101 - for the Debug tab
                  tab: "details",
                })
              }
            >
              <i className="ri-pencil-line align-bottom" /> Edit
            </Button>
            <Button
              color="soft-info"
              size="sm"
              disabled={openingSetupId === card.row.original?.StaffClockKiosk_Id}
              onClick={() =>
                openSetup(
                  card.row.original?.StaffClockKiosk_Id,
                  card.row.original?.StaffClockKiosk_Label
                )
              }
            >
              <i className="ri-qr-code-line align-bottom" /> QR Code
            </Button>
            <Button
              color="soft-danger"
              size="sm"
              onClick={() => setDeleteTarget(card.row.original)}
            >
              <i className="ri-delete-bin-line align-bottom" /> Revoke
            </Button>
          </div>
        ),
      },
    ],
    // openSetup reads kioskUrl as its fallback and openingSetupId for the busy state.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [openingSetupId, kioskUrl]
  );

  return (
    <SettingsPanel
      title="Clock-In Kiosks"
      description="Tablets that staff use to clock in and out at the Clock In/Out kiosk. Create a kiosk token, open its setup link on the tablet, and revoke kiosks you no longer use."
      actions={
        <Button color="primary" onClick={openCreate}>
          <i className="ri-add-line align-bottom me-1" /> New Kiosk
        </Button>
      }
    >
      <Row className="mt-2">
        <Col lg={12}>
          <Card>
            <CardBody className="pt-3">
              <div className="text-muted fs-13 mb-3">
                Kiosk page:{" "}
                <a href={kioskUrl} target="_blank" rel="noopener noreferrer">
                  {kioskUrl}
                </a>
              </div>
              {loading ? (
                <LoadingSpinner />
              ) : kiosks.length ? (
                <TableContainer
                  columns={columns}
                  data={kiosks}
                  isGlobalFilter={false}
                  isAddUserList={false}
                  customPageSize={10}
                  className="custom-header-css"
                  divClass="table-responsive table-card mb-0"
                  tableClass="align-middle table-nowrap"
                  theadClass="text-muted"
                  isLeadsFilter={false}
                />
              ) : (
                <div className="text-muted">
                  No kiosks yet. Create one to set up a clock-in tablet.
                </div>
              )}
            </CardBody>
          </Card>
        </Col>
      </Row>

      {/* Create */}
      <AppModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        title="New Clock-In Kiosk"
        size="md"
        loading={creating}
        onSubmit={handleCreate}
        submitLabel="Create"
        submitDisabled={!label.trim()}
      >
        <Label htmlFor="kiosk-label" className="form-label">
          Kiosk name
        </Label>
        <Input
          id="kiosk-label"
          type="text"
          placeholder="e.g. Warehouse entrance"
          value={label}
          autoFocus
          onChange={(e) => setLabel(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") handleCreate();
          }}
        />
        <div className="text-muted fs-12 mt-2">
          A name helps you recognise which tablet this is when you need to revoke it.
        </div>
        <SiteLocationPicker
          id="kiosk-location"
          sites={sites}
          value={siteLocationId}
          onChange={setSiteLocationId}
        />
      </AppModal>

      {/* Edit (Change #100097) */}
      <AppModal
        open={!!editTarget}
        onClose={() => setEditTarget(null)}
        title="Edit Clock-In Kiosk"
        size="lg"
        loading={saving}
        onSubmit={handleSaveEdit}
        submitLabel="Save"
        submitDisabled={!editTarget?.label?.trim()}
      >
        {/* Change #100101 - Details | Debug */}
        <Nav className="nav-tabs nav-tabs-custom nav-primary mb-3" role="tablist">
          {[
            { id: "details", label: "Details", icon: "ri-edit-line" },
            { id: "debug", label: "Debug", icon: "ri-bug-line" },
          ].map((tab) => (
            <NavItem key={tab.id}>
              <NavLink
                href="#"
                className={classnames("fw-semibold", { active: (editTarget?.tab || "details") === tab.id })}
                onClick={(e) => {
                  e.preventDefault();
                  setEditTarget((t) => ({ ...t, tab: tab.id }));
                }}
              >
                <i className={`${tab.icon} align-bottom me-1`} />
                {tab.label}
              </NavLink>
            </NavItem>
          ))}
        </Nav>

        {editTarget?.tab === "debug" ? (
          <KioskDebugPanel row={editTarget?.row} />
        ) : (
        <>
        <Label htmlFor="kiosk-edit-label" className="form-label">
          Kiosk name
        </Label>
        <Input
          id="kiosk-edit-label"
          type="text"
          value={editTarget?.label || ""}
          onChange={(e) => setEditTarget((t) => ({ ...t, label: e.target.value }))}
        />
        <SiteLocationPicker
          id="kiosk-edit-location"
          sites={sites}
          value={editTarget?.siteLocationId || ""}
          onChange={(v) => setEditTarget((t) => ({ ...t, siteLocationId: v }))}
        />
        {/* Change #100098 - set up a replacement tablet without a new token. */}
        <div className="mt-3 pt-3 border-top d-flex align-items-center justify-content-between gap-2">
          <div className="text-muted fs-12">
            Setting up this kiosk on a tablet again? Show its QR code, token and setup link.
          </div>
          <Button
            color="soft-info"
            size="sm"
            className="flex-shrink-0"
            disabled={!!openingSetupId}
            onClick={() => openSetup(editTarget?.id, editTarget?.label)}
          >
            <i className="ri-qr-code-line align-bottom me-1" /> Show setup QR code
          </Button>
        </div>
        </>
        )}
      </AppModal>

      {/* Setup: straight after creating, or reopened later (Change #100098) */}
      <AppModal
        open={!!reveal}
        onClose={() => setReveal(null)}
        headerAccent="amber"
        title={`Set up "${reveal?.label || "kiosk"}"${reveal?.again ? "" : " now"}`}
        size="lg"
        onSubmit={() => setReveal(null)}
        submitLabel="Done"
        hideCancel
      >
        <p className="mb-3">
          {reveal?.again
            ? "Use any of these to set this kiosk up on a tablet. A tablet already using it keeps working."
            : "Set up the tablet now. You can show this again later from the kiosk's QR Code button."}
        </p>
        <Nav className="nav-tabs nav-tabs-custom nav-primary mb-3" role="tablist">
          {REVEAL_TABS.map((tab) => (
            <NavItem key={tab.id}>
              <NavLink
                href="#"
                className={classnames("fw-semibold", { active: revealTab === tab.id })}
                onClick={(e) => {
                  e.preventDefault();
                  setRevealTab(tab.id);
                }}
              >
                <i className={`${tab.icon} align-bottom me-1`} />
                {tab.label}
              </NavLink>
            </NavItem>
          ))}
        </Nav>

        {reveal && revealTab === "qr" && (
          <div className="text-center mb-3">
            <div className="d-inline-block p-2 bg-white rounded border">
              <KioskQrImage value={kioskSetupLink(reveal.token, reveal.kioskUrl)} size={260} />
            </div>
            <div className="text-muted mt-2">
              On the tablet, open{" "}
              <a href={reveal.kioskUrl} target="_blank" rel="noopener noreferrer">
                {reveal.kioskUrl}
              </a>{" "}
              and choose <strong>Scan QR code</strong>, then hold this screen up to its camera.
            </div>
          </div>
        )}

        {reveal && revealTab === "token" && (
          <>
            <CopyRow
              label="Paste this token into the kiosk setup screen"
              value={reveal.token}
              what="Token"
            />
            <div className="text-muted mb-3">
              On the tablet, open {reveal.kioskUrl}, choose <strong>Enter token</strong> and
              paste it in.
            </div>
          </>
        )}

        {reveal && revealTab === "link" && (
          <>
            <CopyRow
              label="Setup link - open this on the tablet"
              value={kioskSetupLink(reveal.token, reveal.kioskUrl)}
              what="Setup link"
            />
            <div className="text-muted mb-3">
              Opening the link saves the token on that tablet and goes straight to the staff
              picker; nobody needs to log in on it.
            </div>
          </>
        )}

        <div className="text-muted fs-12">
          Anyone with this QR code, token or link can run a kiosk for your company, so
          don't post it anywhere public, and revoke the kiosk here if it is lost.
        </div>
      </AppModal>

      {/* Revoke */}
      <DeleteModal
        show={!!deleteTarget}
        onCloseClick={() => setDeleteTarget(null)}
        onDeleteClick={handleRevoke}
        loading={deleting}
        customText={`Revoke the kiosk "${
          deleteTarget?.StaffClockKiosk_Label || ""
        }"? That tablet will stop working until it is set up again.`}
        customButtonText="Yes, Revoke It"
      />
    </SettingsPanel>
  );
}
